<template>
  <div
    class="table-wrapper-container d-flex flex-column flex-grow-1 overflow-hidden"
    :style="{ height: height, maxHeight: calculatedMaxHeight }"
  >
    <!-- Loading State -->
    <div v-if="loading" class="d-flex justify-center align-center py-16 flex-grow-1">
      <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="items.length === 0"
      class="d-flex flex-column justify-center align-center bg-white border border-gray-100 rounded-lg py-16 flex-grow-1"
    >
      <v-icon size="48" color="grey lighten-1">mdi-database-off</v-icon>
      <span class="text-subtitle-1 font-weight-bold grey--text mt-3">No records found</span>
      <span class="text-caption grey--text">There is no data to display in this view.</span>
    </div>

    <!-- Table Element -->
    <div
      v-else
      class="border border-gray-200 rounded-lg overflow-x-auto overflow-y-auto bg-white shadow-sm flex-grow-1"
    >
      <table class="w-full border-collapse text-sm" :style="{ minWidth: minWidth }">
        <thead class="bg-slate-50 sticky top-0 z-10 border-b border-gray-200">
          <tr>
            <th
              v-for="(col, index) in headers"
              :key="index"
              class="py-3 px-4 grey--text text-xs uppercase font-weight-black tracking-widest border-r border-gray-200"
              :class="
                col.align === 'center'
                  ? 'text-center'
                  : col.align === 'end'
                  ? 'text-right'
                  : 'text-left'
              "
              :style="{ width: col.width || 'auto' }"
            >
              <slot :name="`header.${col.value}`" :header="col">
                {{ col.text }}
              </slot>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, rIndex) in items"
            :key="rIndex"
            class="border-b border-gray-100 hover:bg-slate-50/50 transition-colors"
          >
            <td
              v-for="(col, cIndex) in headers"
              :key="cIndex"
              class="py-3 px-4 border-r border-gray-100 align-middle"
              :class="
                col.align === 'center'
                  ? 'text-center'
                  : col.align === 'end'
                  ? 'text-right'
                  : 'text-left'
              "
              :style="{ width: col.width || 'auto' }"
            >
              <slot :name="`item.${col.value}`" :item="item">
                {{ item[col.value] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TableWrapper',
  props: {
    headers: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    minWidth: {
      type: String,
      default: '1000px'
    },
    height: {
      type: String,
      default: '100%'
    },
    maxHeight: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      windowHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
      offsetTop: 0
    }
  },
  computed: {
    calculatedMaxHeight() {
      if (this.maxHeight) {
        return this.maxHeight
      }
      if (!this.offsetTop) {
        return 'calc(100vh - 320px)'
      }
      const available = this.windowHeight - this.offsetTop - 180
      return `${Math.max(200, available)}px`
    }
  },
  mounted() {
    this.updateOffset()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize)
    }
  },
  methods: {
    onResize() {
      if (typeof window !== 'undefined') {
        this.windowHeight = window.innerHeight
      }
      this.updateOffset()
    },
    updateOffset() {
      if (this.$el) {
        const rect = this.$el.getBoundingClientRect()
        this.offsetTop = rect.top || 0
      }
    }
  }
}
</script>

<style scoped>
th,
td {
  white-space: nowrap;
}
.table-wrapper-container {
  width: 100%;
}
</style>
